import * as React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { observer, inject } from "mobx-react";
import { GitHubAppUIStore } from "./github-app-ui-store";
import { RouteComponentProps } from "react-router";
import {
  InputSize,
  Text,
  Size,
  Radio,
  Formsy,
  Pill,
  Checkbox,
  CheckboxGroup,
  Paragraph,
  Gravatar,
  OrganizationIcon,
  Space,
  MediaObject,
  UserInitialsAvatar,
} from "@root/shared";
import { Grid, Row, Col, RowCol, GridSpacing } from "@root/shared/grid";
import { userStore } from "@root/stores";
import { SuggestedReleaseTypes } from "@root/management/constants/constants";
import { VALIDATIONS, VALIDATION_ERRORS } from "@root/management/utils/formsy/validations";
import { OS, PLATFORMS } from "@lib/common-interfaces";
import { Helmet } from "react-helmet";
import { runInAction, observable } from "mobx";
import { GitHubRepo } from "./data/github-app-model";
import { NewAppUIStore, Owner } from "@root/management/apps/new-app/new-app-ui-store";
import { get } from "lodash";
import { CustomSuggestionInput } from "@root/management/apps/custom-suggestion-input/custom-suggestion-input";

const styles = require("./github-app.scss");

export interface NewAppFormProps {
  githubAppUIStore?: GitHubAppUIStore;
  repo: GitHubRepo;
  githubUserName: string;
}

const NewAppForm = withTranslation("management")(
  inject("githubAppUIStore")(
    observer(
      class NewAppForm extends React.Component<NewAppFormProps & WithTranslation> {
        constructor(props: NewAppFormProps & WithTranslation, context?: any) {
          super(props, context);
          this.autofill(props);
        }

        private autofill(props: NewAppFormProps) {
          const {
            // @ts-ignore. [Should fix it in the future] Strict error.
            githubAppUIStore: { newAppUIStore },
            repo,
          } = props;
          newAppUIStore.language = repo.language;
          const oses = observable.array(NewAppUIStore.getDefaultOSes(props.repo.language!));
          runInAction(() => {
            // This is a bit odd, but calling updateForm with `oses` will automatically
            // make a smart selection of `platform`, but it takes into account the existing
            // value of `platform` as an input. In this case, we’re switching apps, so the
            // previous value of `platform` is not relevant at all, so we need to clear it
            // before setting `oses`. Perhaps a better way would have been to initialize a
            // new instance of NewAppUIStore every time we switch apps ¯\_(ツ)_/¯
            newAppUIStore.updateForm("platform", null);
            newAppUIStore.updateForm("oses", oses);
            newAppUIStore.updateForm("displayName", props.repo.name);
            newAppUIStore.updateForm("description", props.repo.description);
          });
        }

        public UNSAFE_componentWillUpdate(nextProps: NewAppFormProps) {
          if (this.props.repo !== nextProps.repo) {
            this.autofill(nextProps);
          }
        }

        public componentDidMount() {
          this.props.githubAppUIStore!.setSelectedRepo(this.props.repo);
        }

        public componentDidUpdate(prevProps: NewAppFormProps & WithTranslation) {
          if ((!prevProps.repo && this.props.repo) || prevProps.repo.id !== this.props.repo.id) {
            this.props.githubAppUIStore!.setSelectedRepo(this.props.repo);
          }
        }

        get owner(): Owner | void {
          return get<Owner>(this.props, "githubAppUIStore.newAppUIStore.appForm.owner");
        }
        get ownerName() {
          return this.owner ? this.owner.display_name || this.owner.name : null;
        }
        get ownerIcon() {
          if (!this.owner) {
            return null;
          }
          return userStore.currentUser.name === this.owner.name ? ( // If it's the current user, show the Gravatar
            <Gravatar
              email={this.owner.email}
              fallback={this.owner.name ? <UserInitialsAvatar initialsName={this.owner.name} /> : undefined}
            />
          ) : (
            <OrganizationIcon className={styles.icon} organization={this.owner} size={30} />
          );
        }

        public render() {
          const { t, githubAppUIStore } = this.props;
          const { appForm, updateForm, addOrRemoveOS, multiOSValidationMessage } = githubAppUIStore!.newAppUIStore;
          const platforms = NewAppUIStore.getAvailablePlatforms(appForm.oses).filter((platform) => {
            return platform !== PLATFORMS.CORDOVA && platform !== PLATFORMS.UNITY;
          });

          return (
            <>
              <Helmet title="Create app" />
              <Grid rowSpacing={GridSpacing.Medium} bordered>
                <RowCol>
                  <Grid rowSpacing={GridSpacing.Medium}>
                    <RowCol>
                      <Formsy.Input
                        label={`${t("management:common.appName")}:`}
                        type="text"
                        name="display_name"
                        value={appForm.displayName}
                        onChange={(event) => updateForm("displayName", event.target.value)}
                        placeholder={t("management:app.name")}
                        autoCorrect="none"
                        autoCapitalize="none"
                        autoComplete="off"
                        spellCheck="false"
                        isRequired
                        requiredError="Name is required."
                        validations={VALIDATIONS.APP_DISPLAY_NAME}
                        validationErrors={VALIDATION_ERRORS.APP_DISPLAY_NAME}
                        size={InputSize.Large}
                        data-test-id="new-app-input-name"
                      />
                    </RowCol>
                    <Row columnSpacing={GridSpacing.Large}>
                      <Col width={7}>
                        <CustomSuggestionInput
                          items={SuggestedReleaseTypes}
                          value={appForm.release_type || ""}
                          onChange={(value: string) => updateForm("release_type", value)}
                        />
                      </Col>
                      {this.owner && (
                        <Col width={5}>
                          <Grid rowSpacing={GridSpacing.XSmall}>
                            <Row>
                              <Text size={Size.Medium}>Owner:</Text>
                            </Row>
                            <Row>
                              <MediaObject hSpace={Space.XSmall}>
                                {this.ownerIcon}
                                <Text ellipsize size={Size.Medium}>
                                  {this.ownerName}
                                </Text>
                              </MediaObject>
                            </Row>
                          </Grid>
                        </Col>
                      )}
                    </Row>
                  </Grid>
                </RowCol>
                <RowCol>
                  <Grid rowSpacing={GridSpacing.Medium}>
                    <Row>
                      <Col width={4}>
                        <Text size={Size.Medium} id="os-label">
                          OS:
                        </Text>
                      </Col>
                      <Col>
                        <CheckboxGroup validationMessage={multiOSValidationMessage}>
                          <Checkbox checked={appForm.oses.includes(OS.IOS)} onChange={addOrRemoveOS(OS.IOS)} value={OS.IOS}>
                            {OS.IOS}
                          </Checkbox>
                          <Checkbox
                            checked={appForm.oses.includes(OS.ANDROID)}
                            onChange={addOrRemoveOS(OS.ANDROID)}
                            value={OS.ANDROID}
                          >
                            {OS.ANDROID}
                          </Checkbox>
                          <Checkbox
                            checked={appForm.oses.includes(OS.WINDOWS)}
                            onChange={addOrRemoveOS(OS.WINDOWS)}
                            value={OS.WINDOWS}
                          >
                            {OS.WINDOWS}
                          </Checkbox>
                          <Checkbox checked={appForm.oses.includes(OS.MACOS)} onChange={addOrRemoveOS(OS.MACOS)} value={OS.MACOS}>
                            {OS.MACOS}
                            <Pill className={styles.preview}>{t("common:preview")}</Pill>
                          </Checkbox>
                        </CheckboxGroup>
                      </Col>
                    </Row>
                    {appForm.oses.length ? (
                      <Row>
                        <Col width={4}>
                          <Text size={Size.Medium} id="platform-label">
                            Platform:
                          </Text>
                        </Col>
                        <Col>
                          {platforms.length ? (
                            <Formsy.RadioGroup
                              name="platform"
                              aria-labelledby="platform-label"
                              value={appForm.platform}
                              onChange={(platform) => updateForm("platform", platform)}
                              isRequired
                              requiredError="Platform is required."
                            >
                              {platforms.map((platform) => (
                                <Radio key={platform.value} value={platform.value}>
                                  {platform.displayName}
                                </Radio>
                              ))}
                            </Formsy.RadioGroup>
                          ) : (
                            <Paragraph size={Size.Medium}>
                              App Center does not currently support building apps with this combination of operating systems.
                            </Paragraph>
                          )}
                        </Col>
                      </Row>
                    ) : null}
                  </Grid>
                </RowCol>
              </Grid>
            </>
          );
        }
      }
    )
  )
);

type NewAppParams = {
  repoId: string;
};

export const NewApp = withTranslation()(
  // loads the default namespace for the entire app
  inject("githubAppUIStore")(
    observer(
      class NewApp extends React.Component<
        WithTranslation & { githubAppUIStore: GitHubAppUIStore } & RouteComponentProps<NewAppParams, NewAppParams>
      > {
        public render() {
          const { githubAppUIStore } = this.props;
          if (githubAppUIStore.isLoading) {
            return null;
          }

          const repo = githubAppUIStore.getRepoById(this.props.routeParams.repoId);
          const githubUserName = this.props.location.query.github_user_name;
          return <NewAppForm githubUserName={githubUserName} repo={repo!} />;
        }
      }
    )
  )
);
